import {
  Container,
  Graphics,
  Rectangle,
  type FederatedPointerEvent,
  type FederatedWheelEvent,
} from 'pixi.js';
import { COLOR } from './theme.js';

/** Сдвиг пальца, после которого касание считается прокруткой, px кадра. */
const DRAG_THRESHOLD = 10;

/**
 * Вертикальная прокрутка содержимого в окне `width × height` (телефон, SPEC.md 16.3):
 * перетаскивание пальцем или мышью и колесо. Пока идёт перетаскивание, касание не доходит
 * до кнопок внутри — иначе прокрутка нажимала бы карточку под пальцем.
 */
export class ScrollView extends Container {
  readonly content = new Container();
  private readonly maskShape = new Graphics();
  private offset = 0;
  private drag: { y: number; offset: number; moved: boolean } | null = null;

  constructor(
    viewWidth: number,
    private readonly viewHeight: number,
  ) {
    super();
    this.maskShape.rect(0, 0, viewWidth, viewHeight).fill(COLOR.white);
    // Маска — на обёртке: в PixiJS 8 размер контейнера с маской равен маске,
    // а прокрутке нужна настоящая высота содержимого.
    const clip = new Container();
    clip.mask = this.maskShape;
    clip.addChild(this.content);
    this.addChild(this.maskShape, clip);
    this.eventMode = 'static';
    this.hitArea = new Rectangle(0, 0, viewWidth, viewHeight);
    this.on('pointerdown', (e: FederatedPointerEvent) => {
      this.drag = { y: e.global.y, offset: this.offset, moved: false };
    });
    this.on('globalpointermove', (e: FederatedPointerEvent) => {
      const drag = this.drag;
      if (!drag) return;
      const dy = (e.global.y - drag.y) / this.worldTransform.d;
      if (!drag.moved && Math.abs(dy) < DRAG_THRESHOLD) return;
      drag.moved = true;
      this.content.interactiveChildren = false;
      this.scrollTo(drag.offset - dy);
    });
    const end = () => {
      this.drag = null;
      // Кнопки снова доступны после того, как отпускание уже обработано.
      setTimeout(() => {
        if (!this.destroyed) this.content.interactiveChildren = true;
      }, 0);
    };
    this.on('pointerup', end);
    this.on('pointerupoutside', end);
    this.on('wheel', (e: FederatedWheelEvent) => this.scrollTo(this.offset + e.deltaY));
  }

  /** Сколько можно прокрутить: высота содержимого минус окно. */
  private get limit(): number {
    return Math.max(0, this.content.height - this.viewHeight);
  }

  scrollTo(offset: number): void {
    this.offset = Math.max(0, Math.min(this.limit, offset));
    this.content.y = -this.offset;
  }

  /** После перерисовки содержимого: позиция остаётся в допустимых пределах. */
  refresh(): void {
    this.scrollTo(this.offset);
  }
}
